/** Frequency labels → monthly multiplier (quotations / price-list metadata). */
export const FREQ_MULT = {
    Monthly: 1,
    Daily: 30.5,
    'Per day': 30.5,
    Weekly: 4.33,
    'Per week': 4.33,
    Fortnightly: 2.17,
    Hourly: 1,
    '1/2 hourly': 2,
    'Per service': 1,
    'Once-off': 1,
    'Per annum': 1 / 12,
};

export const HOUR_FIELDS = [
    'sn_hours_day',
    'sn_hours_night',
    'ena_hours_day',
    'ena_hours_night',
    'cw_hours_day',
    'cw_hours_night',
];

/** Match CarePlanDetail: top-level → metadata → catalogue.defaults → default 1. */
export function getFrequencyValue(item) {
    if (item == null) return 1;
    if (item.frequency !== undefined && item.frequency !== null && item.frequency !== '' && item.frequency !== 0) {
        return item.frequency;
    }
    const metaFreqDirect = item.metadata?.frequency;
    if (metaFreqDirect != null && metaFreqDirect !== 0 && metaFreqDirect !== '') {
        return metaFreqDirect;
    }
    const metaFreq = item.metadata?.catalogue?.defaults?.frequency;
    if (metaFreq != null && metaFreq !== 0) {
        return metaFreq;
    }
    return 1;
}

/** Match CarePlanDetail hour resolution. */
export function getHoursValue(item, propName) {
    if (item == null) return 0;
    const topLevelValue = item[propName];
    if (topLevelValue != null && topLevelValue !== 0) {
        return topLevelValue;
    }
    if (item.metadata?.[propName] != null && item.metadata[propName] !== 0) {
        return item.metadata[propName];
    }
    if (item.metadata?.catalogue?.defaults?.[propName] != null) {
        return item.metadata.catalogue.defaults[propName];
    }
    return 0;
}

/**
 * Resolve a frequency value to a numeric monthly multiplier.
 * Numeric strings/numbers are used as-is; labels use FREQ_MULT; missing → 1.
 */
export function resolveFrequencyMultiplier(raw) {
    if (raw == null || raw === '') return 1;
    if (typeof raw === 'number' && !Number.isNaN(raw) && raw !== 0) return raw;
    const asNum = parseFloat(raw);
    if (!Number.isNaN(asNum) && String(raw).trim() !== '' && /^-?\d/.test(String(raw).trim())) {
        return asNum === 0 ? 1 : asNum;
    }
    if (FREQ_MULT[raw] != null) return FREQ_MULT[raw];
    return 1;
}

/**
 * Line-item multiplier: keep intentional 0; only null/NaN/missing → 1.
 */
export function resolveMultiplier(raw) {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 1;
}

/** Empty hour totals object. */
export function emptyHourTotals() {
    return Object.fromEntries(HOUR_FIELDS.map((f) => [f, 0]));
}

/**
 * Sum service-plan activity line items into hours (minutes in source ÷ 60).
 * Aligns with CarePlanDetail: frequency default 1, hours from metadata/catalogue.
 */
export function sumServicePlanLineItems(items) {
    const totals = emptyHourTotals();
    (items || []).forEach((item) => {
        const frequency = resolveFrequencyMultiplier(getFrequencyValue(item));
        const multiplier = resolveMultiplier(item.multiplier);
        HOUR_FIELDS.forEach((field) => {
            const mins = parseFloat(getHoursValue(item, field)) || 0;
            totals[field] += (frequency * mins * multiplier) / 60;
        });
    });
    return totals;
}

/**
 * Sum quotation / price-list items (minutes × freq label × multiplier ÷ 60 → hours).
 */
export function sumQuotationLineItems(items) {
    const totals = emptyHourTotals();
    (items || []).forEach((item) => {
        const meta = item.metadata || {};
        const frequency = meta.frequency || item.frequency;
        const freqMult = resolveFrequencyMultiplier(frequency);
        const multiplier = resolveMultiplier(item.multiplier);
        HOUR_FIELDS.forEach((field) => {
            totals[field] += (parseFloat(meta[field] ?? item[field]) || 0) * freqMult * multiplier;
        });
    });
    HOUR_FIELDS.forEach((field) => {
        totals[field] = totals[field] / 60;
    });
    return totals;
}
